import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  RestaurantPrinterDTO,
  RestaurantPrinterForm,
} from '../../../types/restaurant/RestaurantPrinterTypes';
import { PageMetadata } from '../../../types/restaurant/PagedResponse';

export const emptyRestaurantPrinterForm: RestaurantPrinterForm = {
  name: '',
  description: '',
  printerName: '',
  printerType: 'KITCHEN',
  connectionType: 'LOCAL',
  ipAddress: '',
  port: 9100,
  displayOrder: 1,
  enabled: true,
};

interface RestaurantPrinterState {
  list: RestaurantPrinterDTO[];
  page: PageMetadata | null;
  form: RestaurantPrinterForm;
  loading: boolean;
  saving: boolean;
  modalOpen: boolean;
}

const initialState: RestaurantPrinterState = {
  list: [],
  page: null,
  form: emptyRestaurantPrinterForm,
  loading: false,
  saving: false,
  modalOpen: false,
};

const restaurantPrinterSlice = createSlice({
  name: 'restaurantPrinter',
  initialState,
  reducers: {
    setRestaurantPrinters(state, action: PayloadAction<{ content: RestaurantPrinterDTO[]; page: PageMetadata }>) {
      state.list = action.payload.content;
      state.page = action.payload.page;
    },
    setRestaurantPrinterLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setRestaurantPrinterSaving(state, action: PayloadAction<boolean>) {
      state.saving = action.payload;
    },
    openRestaurantPrinterCreate(state) {
      state.form = { ...emptyRestaurantPrinterForm };
      state.modalOpen = true;
    },
    openRestaurantPrinterEdit(state, action: PayloadAction<RestaurantPrinterDTO>) {
      const printer = action.payload;
      state.form = {
        id: printer.id,
        name: printer.name,
        description: printer.description ?? '',
        printerName: printer.printerName,
        printerType: printer.printerType,
        connectionType: printer.connectionType,
        ipAddress: printer.ipAddress ?? '',
        port: printer.port ?? 9100,
        displayOrder: printer.displayOrder,
        enabled: printer.enabled,
        active: printer.active,
      };
      state.modalOpen = true;
    },
    closeRestaurantPrinterModal(state) {
      state.modalOpen = false;
      state.form = { ...emptyRestaurantPrinterForm };
    },
    updateRestaurantPrinterField(
      state,
      action: PayloadAction<{
        field: keyof RestaurantPrinterForm;
        value: RestaurantPrinterForm[keyof RestaurantPrinterForm];
      }>
    ) {
      state.form = { ...state.form, [action.payload.field]: action.payload.value };
      if (
        action.payload.field === 'connectionType' &&
        action.payload.value === 'LOCAL'
      ) {
        state.form.ipAddress = '';
        state.form.port = 9100;
      }
    },
  },
});

export const {
  setRestaurantPrinters,
  setRestaurantPrinterLoading,
  setRestaurantPrinterSaving,
  openRestaurantPrinterCreate,
  openRestaurantPrinterEdit,
  closeRestaurantPrinterModal,
  updateRestaurantPrinterField,
} = restaurantPrinterSlice.actions;

export default restaurantPrinterSlice.reducer;
